import QueryLine from '@/components/query2/query-line';
import isBlank from '@/common/helpers/is-blank';

/**
 * Logic to encode or decode filter data into its string representation for use with the API.
 */
export default class QueryBuilder {
    // TODO criteria support
    // TODO restore support might be deprecated after refactor?
    static EmptyStringFilter = '';

    /**
     * Convert the query array into a filter string
     * @param {[]} query
     * @param {{}} filterSet
     * @return {string|*}
     */
    static convertQueryToFilterString({query, filterSet}) {
        if (query === null || !Array.isArray(query)) {
            return QueryBuilder.EmptyStringFilter;
        }
        return query
            .reduce((result, line) => [...result, QueryBuilder.queryStringReducer({
                filterSet: filterSet || {combinedFilters: {}},
                line,
            })], [])
            .join(QueryLine.StringDelimiters.filter);
    }

    /**
     * Reduce the line value into its encoded form based on the item type.
     * @param {{}} filterSet
     * @param {[]} line
     * @return {string}
     */
    static queryStringReducer({filterSet, line}) {
        const type = QueryBuilder.determineTypeFromFilter({
            filterSet,
            line,
        });
        switch (type) {
            case QueryLine.Types.datetime:
            case QueryLine.Types.numeric:
                return QueryBuilder.encodeRangeToFilter({line});
            default:
                return QueryBuilder.encodeToFilter({line});
        }
    }

    /**
     * Determine the filter type from the line values or fallback to the filter set data.
     * @param {{}} filterSet
     * @param {[]} line
     * @return {string|null}
     */
    static determineTypeFromFilter({filterSet, line}) {
        const [pointer, , type = null] = line;
        if (type) {
            return type;
        }
        if (filterSet.combinedFilters && pointer in filterSet.combinedFilters) {
            return filterSet.combinedFilters[pointer].type;
        }
        // fallback
        return QueryLine.Types.text;
    }

    /**
     * Encode the range data into a string.
     * @param {[]} line
     * @return {string}
     */
    static encodeRangeToFilter({line}) {
        const [pointer, values] = line;
        const [min, max] = values;
        if (values.every(isBlank) || max === undefined) {
            return QueryBuilder.encodeToFilter({line});
        }
        return QueryBuilder.stringifyRange({
            min,
            max,
            pointer,
            converter: v => (v === null ? QueryBuilder.EmptyStringFilter : v),
        });
    }

    /**
     * Encode the line array data into a string.
     * @param {[]} line
     * @return {string}
     */
    static encodeToFilter({line}) {
        const [pointer, values = []] = line;
        return [
            pointer,
            QueryLine.StringDelimiters.definition,
            values.join(QueryLine.StringDelimiters.values),
        ].join('');
    }

    /**
     * Convert the range for the provided pointer into a string.
     * @param {boolean|number|string|null} min
     * @param {boolean|number|string|null} max
     * @param {string} pointer
     * @param {Function} converter Identity method that can be used to convert the value
     * and handle special cases like `null`
     * @return {string}
     */
    static stringifyRange({
        min, max, pointer, converter = v => v,
    }) {
        return [
            pointer,
            QueryLine.StringDelimiters.definition,
            converter(min, true),
            QueryLine.StringDelimiters.range,
            converter(max, false),
        ].join('');
    }

    static convertFilterStringToQuery(filter) {
        return filter
            .trim()
            .split(QueryLine.StringDelimiters.filter)
            .map(QueryBuilder.decodeFilterString)
            .filter(v => v !== null);
    }

    static decodeFilterString(filter) {
        if (filter.length) {
            const [pointer, rawValues] = filter.split(QueryLine.StringDelimiters.definition);
            if (pointer && rawValues) {
                return [pointer, QueryBuilder.decodeFilterValues(rawValues)];
            }
            return [pointer, [QueryBuilder.EmptyStringFilter]];
        }
        return null;
    }

    static decodeFilterValues(rawValues) {
        if (rawValues.includes(QueryLine.StringDelimiters.values)) {
            return rawValues.split(QueryLine.StringDelimiters.values);
        }
        if (rawValues.includes(QueryLine.StringDelimiters.range)) {
            return rawValues
                .split(QueryLine.StringDelimiters.range)
                .map(v => (v === QueryBuilder.EmptyStringFilter ? null : v));
        }
        if (rawValues !== QueryBuilder.EmptyStringFilter) {
            // for equalities
            return [rawValues];
        }
        return [QueryBuilder.EmptyStringFilter];
    }
}
